<template>
  <v-card class="ma-0 pa-3">
    <div class="d-flex align-baseline mb-1">
      <span class="text-h6">2 &middot; Outcome by predictor</span>
      <v-spacer />
      <span class="dd-caption">{{ contextLabel }}</span>
    </div>

    <!-- query strip -->
    <v-sheet outlined rounded class="pa-3 mt-2">
      <div class="d-flex align-center flex-wrap">
        <span class="dd-label mr-2">OUTCOME(S)</span>

        <span v-if="outcomes.length === 0" class="text-body-2 mr-2">
          none selected &mdash; choose outcomes in the toolbar above
        </span>

        <v-chip
          v-for="o in outcomes"
          :key="'obp-o-' + o.uid"
          small
          :color="isOn(deselectedOutcomes, o) ? '' : 'primary'"
          :outlined="isOn(deselectedOutcomes, o)"
          class="mr-2 mb-1"
          @click="toggle(deselectedOutcomes, o)"
        >
          {{ varName(o) }}
        </v-chip>
      </div>

      <div class="d-flex align-center flex-wrap mt-2">
        <span class="dd-label mr-2">PREDICTOR(S)</span>

        <span v-if="eligiblePredictors.length === 0" class="text-body-2 mr-2">
          no categorical predictor available
        </span>

        <v-chip
          v-for="p in eligiblePredictors"
          :key="'obp-p-' + p.uid"
          small
          :color="isOn(deselectedPredictors, p) ? '' : 'primary'"
          :outlined="isOn(deselectedPredictors, p)"
          class="mr-2 mb-1"
          @click="toggle(deselectedPredictors, p)"
        >
          {{ varName(p) }}
        </v-chip>
      </div>

      <div v-if="unusablePredictors > 0" class="dd-caption mt-2">
        <v-icon x-small color="#777" class="mr-1">mdi-information</v-icon>
        {{ unusablePredictors }} selected predictor{{
          unusablePredictors === 1 ? '' : 's'
        }}
        not offered here: continuous, or without two groups of at least
        {{ MIN_GROUP_SIZE }} subjects.
      </div>
    </v-sheet>

    <!-- empty states -->
    <div v-if="predictors.length === 0" :class="emptyStateClass" align="center">
      NO PREDICTORS SELECTED
    </div>

    <div
      v-else-if="eligiblePredictors.length === 0"
      :class="emptyStateClass"
      align="center"
    >
      NO CATEGORICAL PREDICTOR AVAILABLE
    </div>

    <div
      v-else-if="outcomes.length === 0"
      :class="emptyStateClass"
      align="center"
    >
      NO OUTCOMES SELECTED
    </div>

    <div
      v-else-if="activeOutcomes.length === 0 || activePredictors.length === 0"
      :class="emptyStateClass"
      align="center"
    >
      NO TILES SELECTED
    </div>

    <template v-else>
      <div class="dd-caption mt-3">
        Click any plot for a full-size view with group statistics.
      </div>

      <div class="dd-matrix-scroll mt-2">
        <table class="dd-matrix">
          <thead>
            <tr>
              <th class="dd-corner"></th>
              <th
                v-for="p in activePredictors"
                :key="'obp-h-' + p.uid"
                class="dd-colhead"
              >
                {{ varName(p) }}
              </th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="row in matrix" :key="'obp-row-' + row.key">
              <th class="dd-rowhead">
                <div class="dd-rowhead-name">{{ varName(row.outcome) }}</div>

                <!-- one legend for the row, not for each cell: the level
                     colours are shared by every plot beside it, and a copy per
                     cell would take the space the plots are using -->
                <div v-if="row.legend" class="dd-legend">
                  <div
                    v-for="e in row.legend"
                    :key="'obp-lg-' + row.key + '-' + e.level"
                    class="dd-legend-row"
                    :title="e.level"
                  >
                    <span
                      class="dd-swatch"
                      :style="{ backgroundColor: e.color }"
                    ></span>
                    <span class="dd-legend-name">{{ e.level }}</span>
                  </div>

                  <div v-if="row.hiddenLevels > 0" class="dd-legend-more">
                    +{{ row.hiddenLevels }} rarer
                  </div>
                </div>
              </th>
              <td v-for="cell in row.cells" :key="cell.key" class="dd-td">
                <matrix-cell
                  :cell="cell"
                  :row-range="row.range"
                  :stats="statsFor(cell)"
                  @click="openDetail"
                />
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </template>

    <cell-detail-dialog
      v-model="detailOpen"
      :cell="detailCell"
      :stats="detailStats"
      :loading="detailLoading"
    />
  </v-card>
</template>

<script>
import axios from 'axios';
import { mapState } from 'vuex';
import { state } from '@/store/modules/datasetSummary/types';
import {
  DATA_CATEGORY_CONTINUOUS,
  MAX_SPLIT_LEVELS,
  MIN_GROUP_SIZE,
  baselineValuesBySubject,
  boxStats,
  buildAnovaPayload,
  buildTukeyPayload,
  contingencyTable,
  crossTabulate,
  deriveGroups,
  groupStats,
  groupedValues,
  isSplitEligible,
  legendEntries,
  outcomeLevelOrder,
  pairwisePValuesVsReference,
  retainAvailable,
  sharedRange,
} from '@/utils/dashboard';
import { readStoredJson, writeStoredJson } from '@/utils/helpers';
import CellDetailDialog from '@/components/DatasetSummary/DataDashboard/CellDetailDialog.vue';
import MatrixCell from '@/components/DatasetSummary/DataDashboard/MatrixCell.vue';

// statsmodels' pairwise_tukeyhsd needs at least three groups; the existing
// AnalysisSummary caller applies the same guard.
const MIN_GROUPS_FOR_TUKEY = 3;

export default {
  name: 'OutcomeByPredictor',
  components: {
    CellDetailDialog,
    MatrixCell,
  },
  props: {
    // selected outcome variables, each carrying `uid`
    outcomes: {
      type: Array,
      required: true,
    },
    // selected predictor variables, the split-by candidates
    predictors: {
      type: Array,
      required: true,
    },
    longitudinal: {
      type: Boolean,
      default: false,
    },
    contextLabel: {
      type: String,
      default: '',
    },
    // the dataset-scoped localStorage key owned by DataDashboard; this
    // component stores its tile state under a suffix of it
    storageKey: {
      type: String,
      default: null,
    },
  },
  data() {
    return {
      MIN_GROUP_SIZE,
      // tiles are on by default, so tracking the exceptions means a variable
      // newly checked in the toolbar above arrives already selected
      deselectedOutcomes: [],
      deselectedPredictors: [],
      detailOpen: false,
      detailCell: null,
      detailLoading: false,
      // cellKey -> computed statistics, so reopening a drill-down is free
      statsCache: {},
      // discards responses from superseded requests, as in the
      // TIMES_BETWEEN_VISITS_REQUEST_NUM pattern in the datasetSummary store
      requestNum: 0,
    };
  },
  computed: {
    ...mapState('datasetSummary', {
      variableData: state.VARIABLE_DATA,
      visitVariable: state.VISIT_VARIABLE,
      useLongScaleNames: state.USE_LONG_SCALE_NAMES,
    }),
    emptyStateClass() {
      return 'display-1 primary--text text--lighten-5 pt-5 mt-5';
    },
    // categorical predictors that actually resolve to two or more usable
    // groups; the rest are dropped rather than offered as dead tiles
    eligiblePredictors() {
      if (!this.variableData) return [];
      return this.predictors.filter(v => {
        if (v.data_category === DATA_CATEGORY_CONTINUOUS) return false;
        return isSplitEligible(v, this.derivedFor(v).levels.length);
      });
    },
    unusablePredictors() {
      return this.predictors.length - this.eligiblePredictors.length;
    },
    activeOutcomes() {
      return this.outcomes.filter(v => !this.isOn(this.deselectedOutcomes, v));
    },
    activePredictors() {
      return this.eligiblePredictors.filter(
        v => !this.isOn(this.deselectedPredictors, v)
      );
    },
    // One row per active outcome, one cell per active predictor. The heavy
    // per-pair work is cached by cellFor, so toggling a tile only computes the
    // cells that were not on screen already.
    matrix() {
      if (!this.variableData) return [];

      return this.activeOutcomes.map(outcome => {
        const continuous = outcome.data_category === DATA_CATEGORY_CONTINUOUS;
        const cells = this.activePredictors.map(predictor =>
          this.cellFor(outcome, predictor, continuous)
        );

        let range = null;
        if (continuous) {
          const stats = cells.reduce((all, c) => all.concat(c.stats || []), []);
          range = sharedRange(stats);
        }

        const order = continuous ? null : this.outcomeLevelsFor(outcome);

        return {
          key: outcome.uid,
          outcome,
          cells,
          range,
          legend: order ? legendEntries(order.levels) : null,
          hiddenLevels: order ? order.hidden : 0,
        };
      });
    },
    detailStats() {
      if (!this.detailCell) return null;
      return this.statsCache[this.detailCell.key] || null;
    },
  },
  watch: {
    // the key changes when the view loads a different dataset, at which point
    // the previous dataset's tile state no longer applies
    storageKey() {
      this.restoreTiles();
    },
  },
  created() {
    // deliberately not reactive: these are memo tables, not state. Each entry
    // records the variableData it came from so a new dataset invalidates it
    // without needing a watcher to fire first.
    this.baselineCache = new Map();
    this.derivedCache = new Map();
    this.outcomeLevelCache = new Map();
    this.cellCache = new Map();

    this.restoreTiles();
  },
  methods: {
    varName(v) {
      if (!v) return '';
      return this.useLongScaleNames ? v.label : v.abbreviation;
    },
    isOn(deselected, v) {
      return deselected.indexOf(v.uid) !== -1;
    },
    toggle(deselected, v) {
      const i = deselected.indexOf(v.uid);
      if (i === -1) deselected.push(v.uid);
      else deselected.splice(i, 1);
      this.persistTiles();
    },
    tileStorageKey() {
      return this.storageKey ? `${this.storageKey}-tiles` : null;
    },
    restoreTiles() {
      const key = this.tileStorageKey();
      const stored = key ? readStoredJson(key) : null;
      // stale uids are harmless here -- a uid that matches nothing simply
      // deselects nothing -- so they are pruned on write rather than on read
      this.deselectedOutcomes = stored ? stored.outcomes || [] : [];
      this.deselectedPredictors = stored ? stored.predictors || [] : [];
    },
    persistTiles() {
      const key = this.tileStorageKey();
      if (!key) return;
      writeStoredJson(key, {
        outcomes: retainAvailable(this.deselectedOutcomes, this.outcomes),
        predictors: retainAvailable(
          this.deselectedPredictors,
          this.eligiblePredictors
        ),
      });
    },
    // shared shape for the three memo tables: an entry is reusable only while
    // the inputs it was derived from still hold
    memo(cache, key, build) {
      const cached = cache.get(key);
      if (
        cached &&
        cached.source === this.variableData &&
        cached.visitVariable === this.visitVariable &&
        cached.longitudinal === this.longitudinal
      ) {
        return cached.value;
      }

      const value = build();
      cache.set(key, {
        source: this.variableData,
        visitVariable: this.visitVariable,
        longitudinal: this.longitudinal,
        value,
      });
      return value;
    },
    baselineFor(v) {
      return this.memo(this.baselineCache, v.uid, () =>
        baselineValuesBySubject(this.variableData, v, {
          longitudinal: this.longitudinal,
          visitVariable: this.visitVariable,
        })
      );
    },
    derivedFor(predictor) {
      return this.memo(this.derivedCache, predictor.uid, () =>
        deriveGroups(this.baselineFor(predictor))
      );
    },
    // per outcome, not per cell, so every plot in the row colours its levels
    // the same way the row's legend does
    outcomeLevelsFor(outcome) {
      return this.memo(this.outcomeLevelCache, outcome.uid, () =>
        outcomeLevelOrder(this.baselineFor(outcome))
      );
    },
    cellFor(outcome, predictor, continuous) {
      const key = `${outcome.uid}|${predictor.uid}`;
      return this.memo(this.cellCache, key, () => {
        const derived = this.derivedFor(predictor);
        const outcomeValues = this.baselineFor(outcome);

        return {
          key,
          outcome,
          predictor,
          continuous,
          derived,
          outcomeValues,
          levels: derived.levels.map(l => l.level),
          levelCounts: derived.levels,
          referenceLevel: derived.referenceLevel,
          disclosure: this.buildDisclosure(derived),
          stats: continuous
            ? groupedValues(derived, outcomeValues).map(g => boxStats(g.values))
            : null,
          tab: continuous
            ? null
            : crossTabulate(derived, outcomeValues, {
                levelOrder: this.outcomeLevelsFor(outcome).levels,
              }),
        };
      });
    },
    buildDisclosure(derived) {
      if (derived.dropped.length === 0) return '';
      const shown = derived.levels.length;
      const pct = Math.round(derived.coverage * 100);
      return (
        `showing ${shown} of ${derived.totalLevels} levels ` +
        `(${pct}% of subjects with a value). ` +
        `${derived.dropped.length} hidden: fewer than ${MIN_GROUP_SIZE} ` +
        `subjects, or beyond the ${MAX_SPLIT_LEVELS}-level limit.`
      );
    },
    statsFor(cell) {
      return this.statsCache[cell.key] || null;
    },
    // the contingency table is only ever read here, so it is built on open
    // rather than for every cell in the matrix
    async openDetail(cell) {
      this.detailCell = {
        ...cell,
        outcomeName: this.varName(cell.outcome),
        predictorName: this.varName(cell.predictor),
        tableView: cell.continuous
          ? null
          : contingencyTable(cell.derived, cell.outcomeValues, {
              levelOrder: this.outcomeLevelsFor(cell.outcome).levels,
            }),
      };
      this.detailOpen = true;

      if (this.statsCache[cell.key]) return;

      this.requestNum += 1;
      const reqNum = this.requestNum;
      this.detailLoading = true;

      try {
        const stats = await this.computeStats(cell);
        if (reqNum !== this.requestNum) return;
        this.$set(this.statsCache, cell.key, stats);
      } finally {
        if (reqNum === this.requestNum) this.detailLoading = false;
      }
    },
    async computeStats(cell) {
      const stats = {
        pval: null,
        // the test the endpoint chose from the data; the full name rather than
        // the abbreviation, since the drill-down has room for it
        testName: '',
        statRows: cell.continuous
          ? groupStats(cell.derived, cell.outcomeValues)
          : null,
        pairwise: new Map(),
        pairwiseLabel: '—',
        error: null,
      };

      const entry = await this.fetchAnova(cell);
      if (entry) {
        stats.pval = entry.pval == null ? null : entry.pval;
        stats.testName = entry.test_name || entry.test_abbrev || '';
        stats.error = entry.error || null;
      }

      if (cell.continuous) await this.addPairwise(cell, stats);
      return stats;
    },
    async addPairwise(cell, stats) {
      const levels = cell.derived.levels;

      if (levels.length < MIN_GROUPS_FOR_TUKEY) {
        // with exactly two groups the omnibus test is the pairwise comparison
        const other = levels.find(l => l.level !== cell.referenceLevel);
        if (other && stats.pval != null) {
          stats.pairwise = new Map([[other.level, stats.pval]]);
        }
        return;
      }

      const tukey = await this.fetchTukey(cell);
      if (!tukey || !tukey.pvals) {
        stats.pairwiseLabel = 'n/a';
        return;
      }

      stats.pairwise = pairwisePValuesVsReference(
        tukey.unique_groups,
        tukey.pvals,
        cell.referenceLevel
      );
    },
    async fetchAnova(cell) {
      const payload = buildAnovaPayload(
        cell.derived,
        [{ variable: cell.outcome, values: cell.outcomeValues }],
        { longitudinal: this.longitudinal }
      );

      try {
        const { data } = await axios.post('/api/compute-anova', payload);
        // one entry per outputVariable, and this payload carries exactly one
        return data.pvals ? data.pvals[0] : null;
      } catch (err) {
        // the plot and the descriptive statistics are already on screen, so a
        // failure here degrades the test label rather than the whole dialog
        return null;
      }
    },
    async fetchTukey(cell) {
      const payload = buildTukeyPayload(
        cell.derived,
        cell.outcome,
        cell.outcomeValues,
        { longitudinal: this.longitudinal }
      );

      try {
        const { data } = await axios.post(
          '/api/compute-pairwise-tukeyhsd',
          payload
        );
        const results = data.results || data;
        return results[cell.outcome.label] || Object.values(results)[0] || null;
      } catch (err) {
        // this endpoint 500s on some inputs, so a failure here degrades the
        // "vs ref" column rather than the whole dialog
        return null;
      }
    },
  },
};
</script>

<style scoped>
.dd-caption {
  font-size: 12px;
  color: #777;
}
.dd-label {
  font-size: 11px;
  color: #777;
  letter-spacing: 0.6px;
  white-space: nowrap;
}
/* a rectangular matrix can outgrow the viewport in either direction, so the
   headers stay pinned while the cells scroll under them */
.dd-matrix-scroll {
  overflow: auto;
  max-height: 70vh;
  border: 1px solid rgba(0, 0, 0, 0.12);
  border-radius: 4px;
}
.dd-matrix {
  border-collapse: separate;
  border-spacing: 0;
}
.dd-matrix th,
.dd-matrix td {
  background-color: #ffffff;
  border-bottom: 1px solid rgba(0, 0, 0, 0.08);
  border-right: 1px solid rgba(0, 0, 0, 0.08);
}
.dd-td {
  width: 260px;
  min-width: 260px;
  padding: 4px;
  vertical-align: top;
}
.dd-colhead {
  position: sticky;
  top: 0;
  z-index: 2;
  padding: 6px 8px;
  font-size: 12px;
  font-weight: 700;
  color: #333;
  text-align: center;
  white-space: nowrap;
}
/* a fixed width keeps every row's header the same size, so the plots line up
   down the matrix whether or not a row carries a legend */
.dd-rowhead {
  position: sticky;
  left: 0;
  z-index: 1;
  padding: 6px 8px;
  font-size: 12px;
  font-weight: 700;
  color: #333;
  text-align: left;
  vertical-align: top;
  width: 14em;
  max-width: 14em;
}
.dd-rowhead-name {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
/* eight levels of swatch fit inside a cell's height, so a legend normally costs
   the row nothing; only long level names or a full stack push it taller */
.dd-legend {
  margin-top: 6px;
  font-size: 10px;
  font-weight: 400;
  color: #555;
}
.dd-legend-row {
  display: flex;
  align-items: center;
  line-height: 1.35;
}
.dd-swatch {
  flex: 0 0 auto;
  width: 8px;
  height: 8px;
  margin-right: 4px;
  border: 1px solid rgba(0, 0, 0, 0.15);
}
/* the full name is on the row's title attribute when it does not fit */
.dd-legend-name {
  min-width: 0;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.dd-legend-more {
  margin-top: 2px;
  color: #999;
  font-style: italic;
}
.dd-corner {
  position: sticky;
  top: 0;
  left: 0;
  z-index: 3;
}
</style>
